/**
 * A block drawn small, for the palette: its members and the lines between
 * them, from the same scene model the canvas draws (invariant 7), without
 * labels, ports or findings, scaled to fit.
 */
import { memo, useMemo } from "react";
import type { GroupDefinitionDocument } from "../design/document";
import { symbolTransform } from "../render";
import { Glyph } from "../render/glyphs";
import { THEME } from "../render/theme";
import { buildScene, DEFAULT_VIEW } from "../scene";
import type { SceneLibrary } from "../scene/types";
import { asEditable } from "./definition";

const NO_ANNOTATIONS = { labels: false, dimensions: false, fluidLines: true, airLines: true, notes: false, wash: "none" as const };

export const BlockThumbnail = memo(function BlockThumbnail({
  definition,
  library,
  width,
  height,
}: {
  definition: GroupDefinitionDocument;
  library: SceneLibrary;
  width: number;
  height: number;
}) {
  const scene = useMemo(
    () => buildScene(asEditable(definition), library, [], { ...DEFAULT_VIEW, annotations: NO_ANNOTATIONS }),
    [definition, library],
  );
  const { minX, minY, maxX, maxY } = scene.bounds;
  const pad = 8;
  const viewBox = `${minX - pad} ${minY - pad} ${Math.max(1, maxX - minX) + 2 * pad} ${Math.max(1, maxY - minY) + 2 * pad}`;
  return (
    <svg width={width} height={height} viewBox={viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {scene.edges.map((edge) => (
        <polyline
          key={edge.ref}
          points={edge.path.map((point) => `${point.x},${point.y}`).join(" ")}
          fill="none"
          stroke={THEME.channel}
          strokeWidth={3}
          strokeDasharray={edge.air ? "1 6" : undefined}
          strokeLinecap="round"
        />
      ))}
      {scene.nodes.map((node) => (
        <g key={node.ref} transform={symbolTransform(node.centre, node.rotation, node.mirrored)}>
          <Glyph name={node.glyph} size={node.size} />
        </g>
      ))}
    </svg>
  );
});
